import { ApiService } from './api/api'
import type { StockShortageRequest, StockShortageResponse, StockShortageItem, StockCheckResult, TransferOfStockPayload } from '@/data/schema/stock-shortage'
import type { Depot } from '@/data/schema/depot'
import { processParams } from '@/utils/params'
import type { Item } from '@/data/schema/item'

export default class StockService {
	private apiService
	private launchpadApiService

	constructor() {
		this.apiService = new ApiService(true)
		this.launchpadApiService = new ApiService()
	}

	getAll(params?: Record<string, string | string[]>): Promise<any> {
		return this.apiService.get(`stock-id/items`, { params: processParams(params) })
	}

	/**
	 * Get the item or sub group from the item code
	 * @param code string
	 * @returns Promise
	 */
	get(code: string): Promise<{ data: { data: Item } }> {
		return this.apiService.get(`stock-id/items/${code}`)
	}

	/**
	 * Get all depots
	 * @returns Promise<Depot[]>
	 */
	getDepots(): Promise<Depot[]> {
		return this.launchpadApiService.get('/depots?expand=location')
	}

	/**
	 * Get all depot-to-depot distances in a single call
	 * @returns Promise<Record<string, Array<{ depot: string; distance: string; distance_value: number }>>>
	 */
	getDepotDistances(): Promise<any> {
		return this.launchpadApiService.get('/depot-distances')
	}

	/**
	 * Get item stock for all depots
	 * @param itemCodes string[]
	 * @returns Promise
	 */
	getDepotStock(itemCodes: string[]): Promise<any> {
		const codeString = encodeURIComponent(itemCodes.join(','))
		return this.apiService.get(`/stock/all-depots?item_codes=${codeString}`)
	}

	/**
	 * Create a stock shortage request
	 * @param request StockShortageRequest
	 * @returns Promise<StockShortageResponse>
	 */
	createShortageRequest(request: StockShortageRequest): Promise<StockShortageResponse> {
		return this.launchpadApiService.post('/stock/shortage-request', request)
	}

	/**
	 * Get all stock shortage requests with optional filters
	 * @param filters Optional query parameters for filtering
	 * @param assignedDepots Array of depot prefixes to filter by
	 * @returns Promise<StockShortageResponse[]>
	 */
	getShortageRequests(
		filters?: {
			status?: string | string[]
			depot?: string
			contract_number?: string
			search?: string
			myrequests?: boolean
		},
		assignedDepots?: string[]
	): Promise<StockShortageResponse[]> {
		const params = new URLSearchParams()
		if (filters?.status) {
			if (Array.isArray(filters.status)) {
				filters.status.forEach((s) => params.append('status[]', s))
			} else {
				params.append('status[]', filters.status)
			}
		}
		if (filters?.depot) params.append('depot', filters.depot)
		if (filters?.contract_number) params.append('contract_number', filters.contract_number)
		if (filters?.search) params.append('search', filters.search)
		if (filters?.myrequests) params.append('myrequests', '1')

		if (assignedDepots && assignedDepots.length > 0) {
			params.append('assigned_depots', assignedDepots.join('|'))
		}

		const queryString = params.toString()
		return this.launchpadApiService.get(
			`/stock/shortage-request${queryString ? '?' + queryString : ''}`
		)
	}

	/**
	 * Get a single stock shortage request by ID
	 * @param id Request ID
	 * @returns Promise<StockShortageResponse>
	 */
	getShortageRequest(id: string): Promise<StockShortageResponse> {
		return this.launchpadApiService.get(`/stock/shortage-request/${id}`)
	}

	/**
	 * Update a stock shortage request
	 * @param id Request ID
	 * @param data Partial update data
	 * @returns Promise<StockShortageResponse>
	 */
	updateShortageRequest(
		id: string,
		data: Partial<StockShortageResponse>
	): Promise<StockShortageResponse> {
		return this.launchpadApiService.patch(`/stock/shortage-request/${id}`, data)
	}

	/**
	 * Approve a stock shortage request
	 * @param id Request ID
	 * @param approvedBy Name of approver
	 * @returns Promise<StockShortageResponse>
	 */
	approveShortageRequest(id: string, approvedBy: string): Promise<StockShortageResponse> {
		return this.launchpadApiService.post(`/stock/shortage-request/${id}/approve`, {
			approved_by: approvedBy
		})
	}

	/**
	 * Reject a stock shortage request
	 * @param id Request ID
	 * @param rejectedBy Name of rejector
	 * @param notes Rejection notes
	 * @returns Promise<StockShortageResponse>
	 */
	rejectShortageRequest(
		id: string,
		rejectedBy: string,
		notes?: string
	): Promise<StockShortageResponse> {
		return this.launchpadApiService.post(`/stock/shortage-request/${id}/reject`, {
			rejected_by: rejectedBy,
			notes
		})
	}

	/**
	 * Mark a stock shortage request as completed
	 * @param id Request ID
	 * @param completedBy Name of completer
	 * @returns Promise<StockShortageResponse>
	 */
	completeShortageRequest(id: string, completedBy: string): Promise<StockShortageResponse> {
		return this.launchpadApiService.post(`/stock/shortage-request/${id}/complete`, {
			completed_by: completedBy
		})
	}

	/**
	 * Claim a stock shortage request for the current user
	 */
	claimShortageRequest(id: string): Promise<StockShortageResponse> {
		return this.launchpadApiService.post(`/stock/shortage-request/${id}/claim`)
	}

	/**
	 * Release the current user's claim on a stock shortage request
	 */
	releaseShortageRequestClaim(id: string): Promise<StockShortageResponse> {
		return this.launchpadApiService.delete(`/stock/shortage-request/${id}/claim`)
	}

	/**
	 * Update a single item in a stock shortage request
	 * @param itemId Item ID (UID)
	 * @param itemData Item data to update
	 * @returns Promise<StockShortageResponse>
	 */
	updateShortageRequestItem(
		itemId: string,
		itemData: Partial<StockShortageItem>
	): Promise<StockShortageResponse> {
		return this.launchpadApiService.patch(`/stock/shortage-request/items/${itemId}`, itemData)
	}

	/**
	 * Add a new item to an existing stock shortage request (e.g. when splitting an item across depots)
	 * @param requestId Request ID
	 * @param itemData The new item data to add
	 * @returns Promise<StockShortageResponse>
	 */
	addItemToShortageRequest(
		requestId: string,
		itemData: Partial<StockShortageItem>
	): Promise<StockShortageResponse> {
		return this.launchpadApiService.post(`/stock/shortage-request/${requestId}/items`, itemData)
	}

	/**
	 * Delete an item from a stock shortage request
	 * @param requestId Request ID
	 * @param itemId Item ID to delete
	 * @returns Promise<StockShortageResponse>
	 */
	deleteShortageRequestItem(requestId: string, itemId: string): Promise<StockShortageResponse> {
		return this.launchpadApiService.delete(
			`/stock/shortage-request/${requestId}/items/${itemId}`
		)
	}

	/**
	 * Bulk update fulfillment status for multiple items across requests
	 * @param items Array of { id, fulfillment_status, tos_no }
	 * @returns Promise
	 */
	bulkUpdateItemStatus(
		items: Array<{ id: string; fulfillment_status: string; tos_no?: string }>
	): Promise<any> {
		return this.launchpadApiService.post('/stock/shortage-request/bulk-update-items', { items })
	}

	/**
	 * Perform a stock check for specific items at a source depot
	 * @param sourceDepot The depot to check stock at
	 * @param items Array of { item_code, quantity_requested }
	 * @returns Promise<StockCheckResult[]>
	 */
	performStockCheck(
		sourceDepot: string,
		items: Array<{ item_code: string; quantity_requested: number }>
	): Promise<StockCheckResult[]> {
		return this.launchpadApiService.post('/stock/stock-check', {
			source_depot: sourceDepot,
			items
		})
	}

	/**
	 * Confirm a stock check result for a specific item
	 * @param itemId Item ID (UID)
	 * @param stockCheckResult The quantity confirmed by the stock checker
	 * @returns Promise<StockShortageResponse>
	 */
	confirmStockCheck(itemId: string, stockCheckResult: number): Promise<StockShortageResponse> {
		return this.launchpadApiService.patch(`/stock/shortage-request/items/${itemId}`, {
			stock_check_status: 'checked',
			stock_check_result: stockCheckResult,
			stock_checked_at: new Date().toISOString()
		})
	}

	/**
	 * Mark a stock check item as manually checked
	 * @param itemId Item ID (UID)
	 * @returns Promise<StockShortageResponse>
	 */
	markManuallyChecked(itemId: string): Promise<StockShortageResponse> {
		return this.launchpadApiService.patch(`/stock/shortage-request/items/${itemId}`, {
			stock_check_status: 'manually_checked',
			stock_checked_at: new Date().toISOString()
		})
	}

	/**
	 * Get all items that are pending stock check (stock_check_status = 'stock_check_pending')
	 * Returns all shortage requests that contain at least one pending item
	 * @param assignedDepots Array of depot prefixes to filter by
	 * @returns Promise<StockShortageResponse[]>
	 */
	getStockCheckPendingRequests(assignedDepots?: string[]): Promise<StockShortageResponse[]> {
		const params =
			assignedDepots && assignedDepots.length > 0
				? { assigned_depots: assignedDepots.join('|') }
				: {}

		return this.launchpadApiService.get('/stock/stock-check/requests', { params })
	}

	/**
	 * Get counts of outstanding stock check requests per depot and per item
	 * @param assignedDepots Array of depot prefixes to filter by
	 * @returns Promise<any>
	 */
	getStockCheckCounts(assignedDepots?: string[]): Promise<any> {
		const params =
			assignedDepots && assignedDepots.length > 0
				? { assigned_depots: assignedDepots.join('|') }
				: {}

		return this.launchpadApiService.get('/stock/stock-check/counts', { params })
	}

	/**
	 * Create a Transfer of Stock document
	 * @param payload TransferOfStockPayload
	 * @returns Promise with created TOS reference
	 */
	createTransferOfStock(payload: TransferOfStockPayload): Promise<any> {
		return this.apiService.post('/stock/transfer-of-stock', payload)
	}
}
